import { supabase } from "../components/atomic-crm/providers/supabase/supabase";

export type ChatAiRequest = {
  message: string;
  conversationId?: string;
  /** "prospeccao": botão da North IA. "playbook": caixinha das propostas/ficha (exige `playbook`). */
  modoCoach?: "prospeccao" | "playbook";
  /** O servidor lê os dados com a RLS de quem pede; daqui só vai o que abrir. */
  playbook?: { tipo: "consorcio" | "seguro" | "contato"; id: string | number };
};

export type ChatAiResponse = {
  conversationId: string;
  answer: string;
  toolsUsed: Array<{
    name: string;
    ok: boolean;
  }>;
};

function getProjectRef(): string {
  try {
    return new URL(import.meta.env.VITE_SUPABASE_URL).hostname.split(".")[0] || "desconhecido";
  } catch {
    return "desconhecido";
  }
}

function getProjectRefFromIssuer(issuer: string): string | null {
  if (!issuer) return null;

  try {
    return new URL(issuer).hostname.split(".")[0] || null;
  } catch {
    return null;
  }
}

function decodeJwtPayload(token: string): Record<string, any> | null {
  try {
    const parts = token.split(".");
    if (parts.length < 2) return null;
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(base64)
        .split("")
        .map((char) => `%${(`00${char.charCodeAt(0).toString(16)}`).slice(-2)}`)
        .join(""),
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function isLikelyJwtInvalidError(error: unknown): boolean {
  const message = String((error as any)?.message || "").toLowerCase();
  if (!message) return false;

  return /invalid\s*jwt|jwt\s*invalid|jwt\s*malformed|token\s*is\s*expired|expired\s*token|signature|invalid\s*signature|bad\s*jwt/.test(message);
}

async function getValidAccessToken(forceRefresh = false): Promise<string> {
  const { data: sessionData } = await supabase.auth.getSession();
  let session = sessionData.session;

  const nowSeconds = Math.floor(Date.now() / 1000);
  const expiresAt = session?.expires_at ?? 0;
  const isExpiredOrNearExpiry =
    forceRefresh || !session?.access_token || expiresAt <= nowSeconds + 60;

  if (isExpiredOrNearExpiry) {
    const { data: refreshedData, error: refreshError } = await supabase.auth.refreshSession();
    if (refreshError || !refreshedData.session?.access_token) {
      throw new Error("Sessão expirada. Faça login novamente.");
    }
    session = refreshedData.session;
  }

  if (!session?.access_token) {
    throw new Error("Sessão expirada. Faça login novamente.");
  }

  const projectRef = getProjectRef();
  const payload = decodeJwtPayload(session.access_token);
  const tokenIss: string = String(payload?.iss || "");
  const tokenRef = getProjectRefFromIssuer(tokenIss);
  if (tokenRef && tokenRef !== projectRef) {
    throw new Error(
      `JWT de outro projeto (${tokenRef}) detectado. Projeto esperado: ${projectRef}. Faça logout e login.`,
    );
  }

  // Validate JWT against the current project before calling the function.
  // Retry once because getUser can fail transiently (network/auth service hiccups)
  // and that should not be reported as "JWT de outro projeto".
  const validationAttempt1 = await supabase.auth.getUser(session.access_token);
  let validatedUser = validationAttempt1.data;
  let validationError = validationAttempt1.error;

  if (validationError || !validatedUser?.user) {
    const validationAttempt2 = await supabase.auth.getUser(session.access_token);
    validatedUser = validationAttempt2.data;
    validationError = validationAttempt2.error;
  }

  if (validationError || !validatedUser?.user) {
    if (isLikelyJwtInvalidError(validationError)) {
      throw new Error(
        `JWT inválido para o projeto atual (${projectRef}). Faça logout e login novamente.`,
      );
    }

    throw new Error(
      "Não foi possível validar a sessão. Tente novamente em alguns segundos.",
    );
  }

  return session.access_token;
}

async function invokeChatAi(accessToken: string, payload: ChatAiRequest) {
  return supabase.functions.invoke("chat-ai", {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    body: {
      message: payload.message.trim(),
      conversationId: payload.conversationId,
      modoCoach: payload.modoCoach,
      playbook: payload.playbook,
    },
  });
}

export async function sendChatMessage(payload: ChatAiRequest): Promise<ChatAiResponse> {
  if (!payload.message?.trim()) {
    throw new Error("Mensagem vazia.");
  }

  let accessToken = await getValidAccessToken(false);
  let { data, error } = await invokeChatAi(accessToken, payload);

  if (error && /invalid jwt/i.test(error.message || "")) {
    accessToken = await getValidAccessToken(true);
    ({ data, error } = await invokeChatAi(accessToken, payload));
  }

  if (error) {
    let detailedMessage = error.message || "Falha ao consultar o chat de IA";

    const responseContext = (error as any)?.context;
    if (responseContext instanceof Response) {
      try {
        const responseBody = await responseContext.json();
        detailedMessage =
          responseBody?.message ||
          responseBody?.error ||
          detailedMessage;
      } catch {
        // Ignore parse failures and keep the default error message.
      }
    }

    throw new Error(detailedMessage);
  }

  if (!data?.conversationId || !data?.answer) {
    throw new Error("Resposta inválida do endpoint chat-ai.");
  }

  return data as ChatAiResponse;
}
